import { useEffect, useState } from "react";
import { Star, Quote, ExternalLink } from "lucide-react";
import Reveal from "./Reveal";
import { SectionHead } from "./Cards";
import { SITE } from "../data/site";
import { REVIEWS_NOTE } from "../data/content";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GoogleReviews() {
  const [data, setData] = useState(null);
  const { t } = useLang();

  useEffect(() => {
    fetch(`${API}/reviews`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ configured: false, reviews: [] }));
  }, []);

  const reviews = data?.configured && Array.isArray(data.reviews) ? data.reviews : [];

  return (
    <section className="bg-royal-50/60">
      <div className="max-w-7xl mx-auto px-6 py-20" data-testid="reviews-section">
        <SectionHead
          eyebrow={t("revEyebrow")}
          title={t("revTitle")}
          text={reviews.length ? undefined : REVIEWS_NOTE}
        />
        {data?.configured && data.rating != null && (
          <Reveal className="mt-6 text-center">
            <p className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-5 py-2.5 shadow-sm">
              <span className="flex text-gold-dark" aria-label={`${data.rating} out of 5 stars`}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill={i < Math.round(data.rating) ? "currentColor" : "none"} className={i < Math.round(data.rating) ? "" : "text-slate-300"} />
                ))}
              </span>
              <span className="font-heading font-bold text-navy text-sm">{Number(data.rating).toFixed(1)}</span>
              <span className="text-xs text-slate-500">· {data.review_count} Google reviews</span>
            </p>
          </Reveal>
        )}
        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {reviews.length
            ? reviews.map((r, i) => (
                <Reveal key={r.google_maps_uri || i} delay={i * 0.08}>
                  <article className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 h-full flex flex-col">
                    <div className="flex items-center gap-3">
                      {r.author?.photo_uri ? (
                        <img src={r.author.photo_uri} alt="" className="w-10 h-10 rounded-full" loading="lazy" referrerPolicy="no-referrer" />
                      ) : (
                        <span className="w-10 h-10 rounded-full bg-royal text-white flex items-center justify-center font-heading font-bold">
                          {(r.author?.name || "G")[0]}
                        </span>
                      )}
                      <div>
                        <p className="font-heading font-bold text-navy text-sm">{r.author?.name}</p>
                        <span className="flex text-gold-dark" aria-label={`${r.rating} out of 5 stars`}>
                          {[...Array(5)].map((_, s) => (
                            <Star key={s} size={12} fill={s < Math.round(r.rating) ? "currentColor" : "none"} className={s < Math.round(r.rating) ? "" : "text-slate-300"} />
                          ))}
                        </span>
                      </div>
                    </div>
                    <p className="mt-3 text-[13px] text-slate-600 leading-relaxed grow">{r.text}</p>
                    <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                      <span>{r.relative_time}</span>
                      <a href={r.google_maps_uri} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-royal hover:text-gold-dark">
                        View on Google <ExternalLink size={10} />
                      </a>
                    </div>
                  </article>
                </Reveal>
              ))
            : [0, 1, 2].map((i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-6 h-full flex flex-col items-center justify-center text-center min-h-[180px]">
                    <Quote size={26} className="text-gold/60" />
                    <div className="mt-3 flex gap-1 text-slate-300">
                      {[...Array(5)].map((_, s) => <Star key={s} size={14} />)}
                    </div>
                    <p className="mt-3 text-[13px] text-slate-400">{t("revPlaceholder")}</p>
                  </div>
                </Reveal>
              ))}
        </div>
        {data?.configured && <p className="mt-4 text-center text-[10px] text-slate-400">Reviews by Google Maps users, shown in Google's relevance order.</p>}
        <Reveal className="mt-8 text-center">
          <a href={SITE.googleProfile} target="_blank" rel="noopener noreferrer" data-testid="google-profile-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-royal text-royal font-bold text-sm px-7 py-3 hover:bg-royal hover:text-white transition-colors">
            {t("revGoogle")}
          </a>
        </Reveal>
      </div>
    </section>
  );
}
